import debounce from 'lodash.debounce';
import { useEffect, useState } from 'react';

import { ITrackEventProps } from 'typescriptDefs';

import { TLocale } from 'utils/i18n';

import { trackEvent } from './';

interface IScrollDepth {
  0.25: boolean;
  0.5: boolean;
  0.75: boolean;
  1: boolean;
}

export const useScrollY = ({ trackScrollDepth = true }: { trackScrollDepth?: boolean }) => {
  const [scrollY, setScrollY] = useState<number>(0);

  // Adds event listener to set window.scrollY on scroll
  useEffect(() => {
    if (window && trackScrollDepth) {
      const debounceSetScrollY = debounce(() => setScrollY(window.scrollY), 500);
      window.addEventListener('scroll', debounceSetScrollY);
      return () => window.removeEventListener('scroll', debounceSetScrollY);
    }
  }, [trackScrollDepth]);

  return scrollY;
};

export const useScrollDepth = ({
  elementHeight = 0,
  offsetUpToElement = 0,
  scrollY,
  trackScrollDepth = true,
}: {
  elementHeight?: number;
  offsetUpToElement?: number;
  scrollY: number;
  trackScrollDepth?: boolean;
}): IScrollDepth => {
  const [scrollDepth, setScrollDepth] = useState<IScrollDepth>({
    0.25: false,
    0.5: false,
    0.75: false,
    1: false,
  });

  useEffect(() => {
    if (window && trackScrollDepth && elementHeight > 0) {
      // Threshold is reach when bottom of the screen reaches the % threshold of the element's height
      const getThreshold = (scrollDepth: 0.25 | 0.5 | 0.75 | 1): number => {
        return elementHeight * scrollDepth - window.innerHeight + offsetUpToElement;
      };

      const thresholdMainOneQuarter = getThreshold(0.25);
      const thresholdMainTwoQuarter = getThreshold(0.5);
      const thresholdMainThreeQuarter = getThreshold(0.75);
      const thresholdMainFull = getThreshold(1);

      if (!scrollDepth['0.25'] && scrollY > thresholdMainOneQuarter) {
        setScrollDepth({
          0.25: true,
          0.5: false,
          0.75: false,
          1: false,
        });
      } else if (!scrollDepth['0.5'] && scrollY > thresholdMainTwoQuarter) {
        setScrollDepth({
          0.25: true,
          0.5: true,
          0.75: false,
          1: false,
        });
      } else if (!scrollDepth['0.75'] && scrollY > thresholdMainThreeQuarter) {
        setScrollDepth({
          0.25: true,
          0.5: true,
          0.75: true,
          1: false,
        });
      } else if (!scrollDepth['1'] && scrollY > thresholdMainFull) {
        setScrollDepth({
          0.25: true,
          0.5: true,
          0.75: true,
          1: true,
        });
      }
    }
  }, [scrollY, elementHeight, trackScrollDepth]);

  return scrollDepth;
};

export const useTrackScrollDepth = ({
  locale,
  pageName,
  pageVariant,
  scrollDepth,
  trackScrollDepthProps,
}: {
  locale?: TLocale;
  pageName?: string;
  pageVariant?: string;
  scrollDepth: IScrollDepth;
  trackScrollDepthProps?: Partial<ITrackEventProps>;
}): void => {
  useEffect(() => {
    const eventPropsAll = {
      category: pageName || '',
      label: pageVariant || '',
      locale: locale,
      ...trackScrollDepthProps,
    };

    if (scrollDepth['1']) {
      trackEvent('Scroll depth 1 reached', eventPropsAll);
    } else if (scrollDepth['0.75']) {
      trackEvent('Scroll depth 0.75 reached', eventPropsAll);
    } else if (scrollDepth['0.5']) {
      trackEvent('Scroll depth 0.5 reached', eventPropsAll);
    } else if (scrollDepth['0.25']) {
      trackEvent('Scroll depth 0.25 reached', eventPropsAll);
    }
  }, [locale, pageName, pageVariant, scrollDepth]);
};
